/* eslint-disable react-refresh/only-export-components -- the scene library is bundled for the renderer, not hot-reloaded; its helpers live beside the components on purpose */
/**
 * Building blocks every scene shares: the frame (ground colour by tone, safe area by aspect), brand fonts, media,
 * the logo and kinetic type. Motion rules: nothing moves linearly (springs and eased curves only), every element
 * arrives within the first ~0.6 s of its scene, and text stays inside the platform's safe area.
 */
import { createContext, useContext, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { AbsoluteFill, Easing, Img, OffthreadVideo, continueRender, delayRender, interpolate, spring, useCurrentFrame, useVideoConfig } from 'remotion';
import type { SceneTone, VideoAspect, VideoMedia, VideoMotif, VideoTheme } from './spec';
import { MAX_COVER_ZOOM, coverZoom, mediaAspect } from './spec';

// ── Colour ────────────────────────────────────────────────────────────────────

function luminance(hex: string): number {
  const h = hex.replace('#', '');
  if (h.length < 6) return 0.5;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255).map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export const isDark = (hex: string) => luminance(hex) < 0.35;
function mix(hex: string, toward: string, t: number): string {
  const p = (x: string) => [0, 2, 4].map((i) => parseInt(x.replace('#', '').slice(i, i + 2), 16));
  const [a, b] = [p(hex), p(toward)];
  return `#${a.map((v, i) => Math.round(v + (b[i] - v) * t).toString(16).padStart(2, '0')).join('')}`;
}

export interface Palette { ground: string; text: string; muted: string; accent: string; onAccent: string; dark: boolean }

/** The colours of one scene: dark = the brand's ink as the ground, light = its light ground, brand = its colour. */
export function palette(theme: VideoTheme, tone: SceneTone = 'dark'): Palette {
  const darkGround = isDark(theme.ink) ? theme.ink : mix(theme.ink, '#000000', 0.85);
  const lightGround = isDark(theme.bg) ? '#FAFAF7' : theme.bg;
  const ground = tone === 'brand' ? theme.accent : tone === 'light' ? lightGround : darkGround;
  const dark = isDark(ground);
  const text = dark ? (isDark(theme.bg) ? '#FFFFFF' : theme.bg) : (isDark(theme.ink) ? theme.ink : '#111111');
  // The accent must read on this ground; on the brand ground the "accent" is the ink.
  let accent = tone === 'brand' ? text : theme.accent;
  if (tone !== 'brand' && Math.abs(luminance(accent) - luminance(ground)) < 0.25) accent = theme.accent2 && Math.abs(luminance(theme.accent2) - luminance(ground)) >= 0.25 ? theme.accent2 : text;
  const onAccent = isDark(accent) ? '#FFFFFF' : '#111111';
  return { ground, text, muted: dark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.62)', accent, onAccent, dark };
}

// ── Layout ────────────────────────────────────────────────────────────────────

/** Unit: 1 = 1px on a 1080-wide portrait frame; scales so type looks the same in every format. */
/** A scene's text size, set by the person in the editor: every text measure in the scene follows the unit. */
export const TextScaleContext = createContext(1);
/** True when the video has its captions layer (spec.transcript): clips then leave captions to it (no double lines). */
export const CaptionLayerContext = createContext(false);
export function useUnit(): number {
  const { width, height } = useVideoConfig();
  return (Math.min(width, height) / 1080) * useContext(TextScaleContext);
}

export function aspectOf(width: number, height: number): VideoAspect {
  const r = width / height;
  return r < 0.7 ? '9:16' : r < 0.9 ? '4:5' : r < 1.2 ? '1:1' : '16:9';
}

/** Insets that keep text clear of platform UI. 9:16 = Meta's Reels ad safe zones (top 14%, bottom 35% for the ad's
 * caption and CTA bar, 6% each side — more on the right for the action icons); TikTok's are inside these. */
export function useSafeArea(): { top: number; bottom: number; left: number; right: number } {
  const { width, height } = useVideoConfig();
  const u = Math.min(width, height) / 1080;
  const a = aspectOf(width, height);
  if (a === '9:16') return { top: 270 * u, bottom: 670 * u, left: 65 * u, right: 120 * u };
  if (a === '4:5') return { top: 96 * u, bottom: 140 * u, left: 72 * u, right: 72 * u };
  return { top: 80 * u, bottom: 80 * u, left: 96 * u, right: 96 * u };
}

export function SafeArea({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  const s = useSafeArea();
  return <AbsoluteFill style={{ paddingTop: s.top, paddingBottom: s.bottom, paddingLeft: s.left, paddingRight: s.right, ...style }}>{children}</AbsoluteFill>;
}

// ── Fonts ─────────────────────────────────────────────────────────────────────

const requested = new Set<string>();
/** Load the brand faces before the first frame is captured (the renderer waits on delayRender). */
export function useBrandFonts(theme: VideoTheme): void {
  const [handle] = useState(() => (typeof document === 'undefined' ? null : delayRender('Loading brand fonts', { timeoutInMilliseconds: 20000 })));
  useEffect(() => {
    if (handle === null) return;
    let done = false;
    const finish = () => { if (!done) { done = true; continueRender(handle); } };
    if (theme.fontHref && !requested.has(theme.fontHref)) {
      requested.add(theme.fontHref);
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = theme.fontHref;
      document.head.appendChild(link);
    }
    const faces = [...new Set([theme.headingFont, theme.bodyFont].filter(Boolean))];
    const timer = setTimeout(finish, 6000); // a missing face falls back; never hang the render
    Promise.all(faces.flatMap((f) => [document.fonts.load(`700 64px "${f}"`), document.fonts.load(`400 32px "${f}"`)]))
      .then(() => document.fonts.ready).catch(() => undefined).finally(() => { clearTimeout(timer); finish(); });
    return () => { clearTimeout(timer); finish(); };
  }, [handle, theme.fontHref, theme.headingFont, theme.bodyFont]);
}

export const headingStack = (t: VideoTheme) => `"${t.headingFont}", "Helvetica Neue", Arial, sans-serif`;
export const bodyStack = (t: VideoTheme) => `"${t.bodyFont}", "Helvetica Neue", Arial, sans-serif`;

// ── Motion ────────────────────────────────────────────────────────────────────

/** 0 → 1 with a soft spring, starting `delay` frames into the scene. */
export function useEnter(delay = 0, damping = 18): number {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  return spring({ frame: frame - delay, fps, config: { damping, mass: 0.7, stiffness: 140 } });
}

/** 1 → 0 over the last `frames` frames of a scene of `total` frames (for exits that aren't transitions). */
export function useExit(total: number, frames = 8): number {
  const frame = useCurrentFrame();
  return interpolate(frame, [total - frames, total], [1, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: Easing.in(Easing.cubic) });
}

/** A slow push-in for stills (never linear). */
export function useKenBurns(total: number, from = 1.04, to = 1.12): number {
  const frame = useCurrentFrame();
  return interpolate(frame, [0, total], [from, to], { extrapolateRight: 'clamp', easing: Easing.inOut(Easing.quad) });
}

// ── Media ─────────────────────────────────────────────────────────────────────

/** A clip's own sound: muted, or at its normalised gain with short fades at both ends (no clicks, no hard audio cuts). */
export function clipVolume(media: VideoMedia, voice: 'keep' | 'mute' | undefined, total: number): (f: number) => number {
  if (voice === 'mute') return () => 0;
  const gain = media.volume ?? 1;
  const fade = 5;
  return (f: number) => gain * interpolate(f, [0, fade, Math.max(fade + 1, total - fade), Math.max(fade + 2, total)], [0, 1, 1, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
}

/** True where the video is only looked at (the editor's preview, layout and review stills): clips play their light
 * proxy. An exported MP4 never sets it, so it always plays the original files. */
export const ProxyContext = createContext(false);
/** Clips already downloaded by the editor (file URL → local blob URL): they play at once, no buffering. */
export const SourcesContext = createContext<Record<string, string> | null>(null);

export function Media({ media, total, dim = 0, blur = 0, style, voice }: { media: VideoMedia; total: number; dim?: number; blur?: number; style?: CSSProperties; voice?: 'keep' | 'mute' }) {
  const { fps } = useVideoConfig();
  const scale = useKenBurns(total);
  const proxy = useContext(ProxyContext);
  const sources = useContext(SourcesContext);
  // The deck's media rule: always cover; a video is centred (its subject moves), a photo keeps its focus point inside
  // the middle band (25–75%) so a crop never slams the picture to its edge.
  const fit = media.fit || 'cover';
  const pos = media.kind === 'video' ? `${Math.max(0, Math.min(100, media.focusX ?? 50))}% 50%` : `50% ${Math.max(25, Math.min(75, media.focusY ?? 40))}%`;
  // Blur (behind a hook line) also scales up a touch so the soft edge never shows.
  const common: CSSProperties = { width: '100%', height: '100%', objectFit: fit, objectPosition: pos, ...(blur ? { filter: `blur(${blur}px) saturate(1.1)`, transform: 'scale(1.08)' } : {}) };
  return (
    <AbsoluteFill style={{ overflow: 'hidden', ...style }}>
      {media.kind === 'video' ? (
        // In the editor's preview a clip still loading pauses playback for a moment instead of showing black (Milo,
        // 2026-10-08: "parts go black, the picture comes later"). Renders always wait for every frame anyway.
        <OffthreadVideo src={(() => { const u = proxy && media.proxyUrl ? media.proxyUrl : media.url; return sources?.[u] || u; })()} trimBefore={Math.round((media.trimStart || 0) * fps)} playbackRate={media.playbackRate || 1} volume={clipVolume(media, voice, total)} style={common} pauseWhenBuffering />
      ) : (
        <Img src={media.url} style={{ ...common, transform: `scale(${scale * (blur ? 1.08 : 1)})` }} />
      )}
      {dim > 0 && <AbsoluteFill style={{ background: `linear-gradient(180deg, rgba(0,0,0,${dim * 0.55}) 0%, rgba(0,0,0,${dim * 0.15}) 40%, rgba(0,0,0,${dim * 0.75}) 100%)` }} />}
    </AbsoluteFill>
  );
}

// ── Logo ──────────────────────────────────────────────────────────────────────

export function Logo({ theme, onDark, height, style }: { theme: VideoTheme; onDark: boolean; height: number; style?: CSSProperties }) {
  const { width: frameWidth } = useVideoConfig();
  const src = onDark ? theme.logoOnDarkUrl || theme.logoUrl : theme.logoUrl || theme.logoOnDarkUrl;
  if (!src) {
    return <div style={{ fontFamily: headingStack(theme), fontWeight: 800, fontSize: height * 0.9, lineHeight: 1, letterSpacing: '-0.03em', ...style }}>{theme.brandName}</div>;
  }
  // Only a logo for the other ground: show it in the text colour so it never disappears.
  const wrongTone = onDark ? !theme.logoOnDarkUrl : !theme.logoUrl;
  // Wide wordmarks (7:1 and more) take the height only while they fit: never wider than ~78% of the FRAME. In pixels —
  // a percentage would be of the wrapper, which shrinks to the logo: the image then drew narrower than its box, pinned
  // left, while everything else on the end card sat centred.
  return <Img src={src} style={{ display: 'block', maxHeight: height, height, width: 'auto', maxWidth: Math.round(frameWidth * 0.78), objectFit: 'contain', filter: wrongTone ? (onDark ? 'brightness(0) invert(1)' : 'brightness(0)') : undefined, ...style }} />;
}

// ── Kinetic type ──────────────────────────────────────────────────────────────

const norm = (w: string) => w.toLowerCase().replace(/[^\p{L}\p{N}%]/gu, '');

/** Words rise in one after another; emphasised words take the accent as a marker behind them. */
export function KineticText({ text, size, color, accent, onAccent, font, weight = 800, delay = 0, stagger = 3, emphasis = [], align = 'left', lineHeight = 1.02, marker = true, instant = false }: {
  text: string; size: number; color: string; accent: string; onAccent: string; font: string; weight?: number; delay?: number; stagger?: number;
  emphasis?: string[]; align?: 'left' | 'center'; lineHeight?: number; marker?: boolean;
  /** Every word fully there from frame 0 (hooks: the first frame is the thumbnail); motion comes from the wrapper. */
  instant?: boolean;
}) {
  const frame = useCurrentFrame();
  const { fps, width } = useVideoConfig();
  const words = text.split(/\s+/).filter(Boolean);
  const em = new Set(emphasis.flatMap((e) => e.split(/\s+/)).map(norm));
  // A long compound ("hushållsprodukter", ICA 2026-10-09) never runs off the frame: the size comes down until the
  // longest word fits ~84% of the width (a heavy grotesk is ~0.58 em a letter).
  const longest = Math.max(1, ...words.map((w) => w.length));
  size = Math.min(size, (width * 0.84) / (longest * 0.58));
  return (
    // Inline words (not flex items): only inline text is balanced by text-wrap, so a heading never leaves one word
    // alone on its last line ("Agility Peak / 6"). The frame review flagged these orphans on every brand.
    <div style={{ fontFamily: font, fontSize: size, fontWeight: weight, lineHeight, letterSpacing: '-0.025em', color, textAlign: align, textWrap: 'balance' as CSSProperties['textWrap'], overflowWrap: 'break-word' }}>
      {words.map((w, i) => {
        const p = instant ? 1 : spring({ frame: frame - delay - i * stagger, fps, config: { damping: 16, mass: 0.6, stiffness: 160 } });
        const hit = em.has(norm(w));
        return (
          <span key={i} style={{ display: 'inline-block', opacity: p, transform: `translateY(${(1 - p) * size * 0.45}px)`, position: 'relative', isolation: 'isolate', color: hit ? (marker ? onAccent : accent) : color, padding: hit && marker ? `0 ${size * 0.08}px` : undefined }}>
            {hit && marker && <span style={{ position: 'absolute', inset: `${size * 0.06}px 0 ${size * 0.02}px 0`, background: accent, borderRadius: size * 0.08, transform: `scaleX(${p})`, transformOrigin: 'left', zIndex: -1 }} />}
            {w}
          </span>
        );
      }).flatMap((el, i) => (i ? [' ', el] : [el]))}
    </div>
  );
}

/** A small label above a statement (uppercase, tracked). */
export function Eyebrow({ text, theme, color, size }: { text: string; theme: VideoTheme; color: string; size: number }) {
  const p = useEnter(0);
  return <div style={{ fontFamily: bodyStack(theme), fontSize: size, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color, opacity: p, transform: `translateY(${(1 - p) * 12}px)` }}>{text}</div>;
}

/** Fades a block in from below after `delay` frames. */
export function Rise({ delay = 0, children, style }: { delay?: number; children: ReactNode; style?: CSSProperties }) {
  const p = useEnter(delay);
  return <div style={{ opacity: p, transform: `translateY(${(1 - p) * 28}px)`, ...style }}>{children}</div>;
}

// ── Art direction ─────────────────────────────────────────────────────────────

export const MotifContext = createContext<VideoMotif>('none');
export const useMotif = () => useContext(MotifContext);

/** The 'rule' motif: a short accent bar that grows in above a headline (scenes call this; it draws nothing otherwise). */
export function MotifRule({ color, u, delay = 0 }: { color: string; u: number; delay?: number }) {
  const motif = useMotif();
  const p = useEnter(delay, 16);
  if (motif !== 'rule') return null;
  return <div style={{ width: 120 * u * p, height: 10 * u, borderRadius: 5 * u, background: color, marginBottom: 28 * u }} />;
}

// ── Frames inside the frame (landscape: the brand stage, creator cards, splits) ─────────────────────────────────

/**
 * The box a scene draws in. Full frame normally; beside the brand stage it is the rest of the frame, and `framed` is
 * true — the scene's own ground is then transparent (the stage's brand ground runs under everything).
 */
export interface Region { width: number; height: number; framed: boolean }
export const RegionContext = createContext<Region | null>(null);
export function useRegion(): Region {
  const r = useContext(RegionContext);
  const { width, height } = useVideoConfig();
  return r || { width, height, framed: false };
}

/** Whether `media` should be framed as a card here rather than fill the box (the footage rule, MAX_COVER_ZOOM). */
export function shouldFrame(media: VideoMedia, region: Region): boolean {
  if (region.framed && media.kind === 'video') return true;
  const ar = mediaAspect(media);
  return ar != null && coverZoom(ar, region.width / region.height) > MAX_COVER_ZOOM;
}

/** The brand ground for landscape layouts: the brand colour as a soft, slowly drifting gradient. */
export function BrandGround({ theme }: { theme: VideoTheme }) {
  const frame = useCurrentFrame();
  const a = theme.accent; const b = theme.accent2 || mix(theme.accent, '#FFFFFF', 0.45);
  const light = mix(a, '#FFFFFF', 0.55);
  const drift = Math.sin(frame / 90) * 8;
  return <AbsoluteFill style={{ background: `radial-gradient(120% 90% at ${30 + drift}% ${35 - drift / 2}%, ${light} 0%, ${a} 55%, ${mix(b, a, 0.5)} 100%)` }} />;
}

const initials = (s: string) => s.replace(/^@/, '').split(/[\s._-]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');

/**
 * A creator's clip as a post-like card: their own shape (no crop), a header with their name and handle, rounded at
 * the top and running off the bottom edge like a feed under the screen. `height` is the card's full height.
 */
export function CreatorCard({ media, total, voice, height, theme, dim = 0, children, highlight = false, bleed = true }: {
  media: VideoMedia; total: number; voice?: 'keep' | 'mute'; height: number; theme: VideoTheme; dim?: number; children?: ReactNode; highlight?: boolean; bleed?: boolean;
}) {
  const u = useUnit();
  const ar = mediaAspect(media) || 9 / 16;
  const w = Math.round(height * ar);
  const radius = 28 * u;
  const handle = media.creatorHandle?.replace(/^@/, '');
  const name = media.creatorName || null;
  const enter = useEnter(0, 20);
  return (
    <div style={{ position: 'relative', width: w, height, borderRadius: bleed ? `${radius}px ${radius}px 0 0` : radius, overflow: 'hidden', background: '#000',
      boxShadow: `0 ${18 * u}px ${48 * u}px rgba(0,0,0,0.28)${highlight ? `, 0 0 0 ${6 * u}px ${theme.accent}` : ''}`,
      transform: `translateY(${(1 - enter) * 40 * u}px)`, opacity: Math.min(1, enter * 1.4) }}>
      <Media media={{ ...media, fit: 'cover' }} total={total} voice={voice} dim={dim} />
      {(handle || name) && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: `${18 * u}px ${22 * u}px`, display: 'flex', alignItems: 'center', gap: 14 * u, background: 'linear-gradient(180deg, rgba(0,0,0,0.45), rgba(0,0,0,0))' }}>
          <div style={{ width: 52 * u, height: 52 * u, borderRadius: 999, background: theme.accent, color: isDark(theme.accent) ? '#FFF' : '#111', display: 'grid', placeItems: 'center', fontFamily: headingStack(theme), fontWeight: 800, fontSize: 22 * u, border: `${3 * u}px solid rgba(255,255,255,0.9)` }}>{initials(name || handle || '')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
            {name && <span style={{ fontFamily: bodyStack(theme), fontWeight: 700, fontSize: 26 * u, color: '#FFF' }}>{name}</span>}
            {handle && <span style={{ fontFamily: bodyStack(theme), fontWeight: name ? 500 : 700, fontSize: (name ? 22 : 26) * u, color: 'rgba(255,255,255,0.88)' }}>@{handle}</span>}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * A product picture (packshots come on white). On a near-white ground the white melts into it (multiply). On any
 * coloured ground the picture sits on a light plate in its TRUE colours — multiply over a coloured ground would tint
 * the product itself (an orange ground turned a white-teal-pink shoe orange and green: a false product).
 */
export function Packshot({ media, ground, total, height, style }: { media: VideoMedia; ground: string; total: number; height: number | string; style?: CSSProperties }) {
  const u = useUnit();
  const nearWhite = luminance(ground) > 0.82;
  const img = <Media media={{ ...media, fit: 'contain' }} total={total} style={nearWhite ? { mixBlendMode: 'multiply', filter: 'brightness(1.07) contrast(1.06)' } : {}} />;
  // Packshots arrive trimmed to the product (backend packshot.ts), so the slow zoom needs its own margin, and the
  // multiply needs the ground painted in its own layer to melt the white into (on a transparent layer it shows a box).
  if (nearWhite) return <div style={{ position: 'relative', height, width: '100%', background: ground, isolation: 'isolate', ...style }}><div style={{ position: 'absolute', inset: '6%' }}>{img}</div></div>;
  return (
    <div style={{ position: 'relative', height, width: '100%', ...style }}>
      <div style={{ position: 'absolute', inset: 0, borderRadius: 40 * u, background: '#FFFFFF', boxShadow: `0 ${18 * u}px ${44 * u}px rgba(0,0,0,0.18)` }} />
      <div style={{ position: 'absolute', inset: '7%' }}>{img}</div>
    </div>
  );
}
